import { styled, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import { Typography } from '../../../../components/Typography/Typography';
import { UserRole } from '../../../../types';
import { roleLabel } from '../../../../utils/roleLabels';
import { IUserFileds } from './UserModal';

type Props = {
  validateForm?: () => void;
};

/**
 * Card-grid role picker (design refresh of the old chip-based `RolesSection`). Purely presentational
 * change: it toggles the same `roles` Formik field with identical multi-select semantics, so behaviour,
 * validation and payloads are unchanged. `RolesSection` is left in place for any other usages.
 */
const ROLE_DESCRIPTION: Record<UserRole, string> = {
  [UserRole.OPERATING_PARTNER]: 'This person submits timesheets.',
  // A proxy fills timesheets in by default; actually submitting them is a per-submitter permission
  // granted by the "Allow submitting timesheets" switch, so the role itself only promises populating.
  [UserRole.PROXY]: 'This user populates timesheets on behalf of the submitter.',
  [UserRole.OP_MANAGER]: "Reviews the submitter's timesheets.",
  [UserRole.DEAL_OWNER]: 'Reviews deal timesheets.',
  [UserRole.ACCOUNTANT]: 'Reviews all timesheets.',
  [UserRole.ADMIN]: 'Has access to the management of the system.',
};

// Display order mirrors the enum / proposal grid: Submitter, Proxy, Manager, Deal Owner, Accountant, Admin.
const ROLE_ORDER: UserRole[] = [
  UserRole.OPERATING_PARTNER,
  UserRole.PROXY,
  UserRole.OP_MANAGER,
  UserRole.DEAL_OWNER,
  UserRole.ACCOUNTANT,
  UserRole.ADMIN,
];

export function RolesCardPicker({ validateForm }: Props) {
  const { colors } = useTheme();
  const { setFieldValue, values, errors, touched, setFieldTouched } =
    useFormikContext<IUserFileds>();

  const selectedRoles = values.roles ?? [];
  const showError = touched.roles && !!errors.roles;

  const toggleRole = async (role: UserRole) => {
    const current = values.roles ?? [];
    const next = current.includes(role)
      ? current.filter((r) => r !== role)
      : [...current, role];
    await setFieldValue('roles', next);
    setFieldTouched('roles', true, false);
    validateForm && (await validateForm());
  };

  return (
    <Section>
      <Typography variant="overline" color={colors.text.caption} style={{ letterSpacing: '0.08em' }}>
        ROLES
      </Typography>
      <Grid>
        {ROLE_ORDER.map((role) => {
          const selected = selectedRoles.includes(role);
          return (
            <Card
              key={role}
              type="button"
              $selected={selected}
              onClick={() => toggleRole(role)}
              aria-pressed={selected}
            >
              <Checkbox $selected={selected} aria-hidden>
                {selected && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12.5l4.5 4.5L19 7"
                      stroke={colors.textAccent.inverse}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </Checkbox>
              <TextWrap>
                <Typography variant="subtitle1" color={colors.text.main}>
                  {roleLabel(role)}
                </Typography>
                <Typography variant="caption" color={colors.text.secondary}>
                  {ROLE_DESCRIPTION[role]}
                </Typography>
              </TextWrap>
            </Card>
          );
        })}
      </Grid>
      {showError && (
        <Typography variant="caption" color={colors.iconStatus.error}>
          {String(errors.roles)}
        </Typography>
      )}
    </Section>
  );
}

const Section = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Grid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled('button')<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.colors.surfaceInteraction.selectedLight : theme.colors.surfaceBackground.bg4};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.surfaceAccent.default : theme.colors.border.default};
  transition: border-color 0.15s ease, background-color 0.15s ease;
  &:hover {
    border-color: ${({ theme }) => theme.colors.surfaceAccent.default};
  }
`;

const Checkbox = styled('span')<{ $selected: boolean }>`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.colors.surfaceAccent.default : 'transparent'};
  border: 1.5px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.surfaceAccent.default : theme.colors.textAccent.disabled};
`;

const TextWrap = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
